import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ReleaseRow {
  readonly releaseId: string;
  readonly releaseCode: string;
  readonly releaseName: string;
  readonly releaseType: string;
  readonly version: string | null;
  readonly status: string;
  readonly rollbackPlan: string | null;
  readonly scheduledStartAt: string | null;
}

const TYPES = ['', 'MAJOR', 'MINOR', 'PATCH', 'HOTFIX', 'EMERGENCY', 'MAINTENANCE', 'CONFIGURATION'];
const STATUSES = ['', 'PLANNED', 'PACKAGED', 'SCHEDULED', 'APPROVED', 'DEPLOYED', 'VALIDATED', 'CLOSED', 'CANCELLED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M32 — Release Management Dashboard (§31 / §11-13). Register of release trains
 * across the eight-state lifecycle with type, semantic version and rollback-plan
 * presence (BR-03 gate for production). Supports authoring a release and driving
 * the packaging → schedule → approval → deploy → validate → close pipeline (and
 * cancel), each wired to the guarded /v1/devops/releases write API. Filterable by
 * release type and status.
 */
export function ReleaseManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ReleaseRow[]>([]);
  const [releaseType, setReleaseType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('MINOR');
  const [newVersion, setNewVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (releaseType) params.set('releaseType', releaseType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ReleaseRow[] }>(`/v1/devops/releases${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, releaseType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.release.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m32.release.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/releases`, {
        method: 'POST',
        body: { releaseName: newName.trim(), releaseType: newType, version: newVersion.trim() || undefined },
        token,
      });
      setNotice(tr('m32.release.createdNotice', { name: newName.trim() }));
      setNewName('');
      setNewVersion('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: ReleaseRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/releases/${r.releaseId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(tr('m32.release.actNotice', { code: r.releaseCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="release-management">
      <h1>{tr('m32.release.title')}</h1>

      <section aria-label="create-release">
        <h2>{tr('m32.release.createTitle')}</h2>
        <form aria-label="release-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.release.fieldName')}<input aria-label="field-release-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m32.release.type')}
            <select aria-label="field-release-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m32.release.fieldVersion')}<input aria-label="field-version" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} /></label>
          <button type="submit" aria-label="submit-release" disabled={busy}>{busy ? tr('m32.release.creating') : tr('m32.release.createBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="release-error">{actionError}</p>}
        {notice && <p aria-label="release-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.release.type')}
          <select aria-label="filter-release-type" value={releaseType} onChange={(e) => setReleaseType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.release.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.release.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.release.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.release.loading')}</p> : (
        <>
          <p aria-label="release-count">{tr('m32.release.count', { count: rows.length })}</p>
          <table aria-label="release-table">
            <thead><tr><th>{tr('m32.release.thCode')}</th><th>{tr('m32.release.fieldName')}</th><th>{tr('m32.release.type')}</th><th>{tr('m32.release.fieldVersion')}</th><th>{tr('m32.release.thRollbackPlan')}</th><th>{tr('m32.release.status')}</th><th>{tr('m32.release.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.releaseId} data-status={r.status}>
                  <td>{r.releaseCode}</td><td>{r.releaseName}</td><td>{r.releaseType}</td><td>{r.version ?? '—'}</td>
                  <td>{r.rollbackPlan ? 'YES' : 'no'}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'PLANNED' && (
                      <button type="button" aria-label={`package-${r.releaseCode}`} onClick={() => void act(r, 'package', tr('m32.release.act.packaged'))}>{tr('m32.release.package')}</button>
                    )}
                    {r.status === 'PACKAGED' && (
                      <button type="button" aria-label={`schedule-${r.releaseCode}`} onClick={() => void act(r, 'schedule', tr('m32.release.act.scheduled'))}>{tr('m32.release.schedule')}</button>
                    )}
                    {r.status === 'SCHEDULED' && (
                      <button type="button" aria-label={`approve-${r.releaseCode}`} onClick={() => void act(r, 'approve', tr('m32.release.act.approved'))}>{tr('m32.release.approve')}</button>
                    )}
                    {r.status === 'APPROVED' && (
                      <button type="button" aria-label={`deploy-${r.releaseCode}`} onClick={() => void act(r, 'deploy', tr('m32.release.act.deployed'))}>{tr('m32.release.deploy')}</button>
                    )}
                    {r.status === 'DEPLOYED' && (
                      <button type="button" aria-label={`validate-${r.releaseCode}`} onClick={() => void act(r, 'validate', tr('m32.release.act.validated'))}>{tr('m32.release.validate')}</button>
                    )}
                    {r.status === 'VALIDATED' && (
                      <button type="button" aria-label={`close-${r.releaseCode}`} onClick={() => void act(r, 'close', tr('m32.release.act.closed'))}>{tr('m32.release.close')}</button>
                    )}
                    {!['CLOSED', 'CANCELLED', 'VALIDATED'].includes(r.status) && (
                      <button type="button" aria-label={`cancel-${r.releaseCode}`} onClick={() => void act(r, 'cancel', tr('m32.release.act.cancelled'))}>{tr('m32.release.cancel')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
